import type {EventMessage} from "./IntegrationEventTypes.ts";
import {EventType} from "./IntegrationEventTypes.ts";
import useStore from "../store.ts";
import {packMonitorAdditions} from "../utils/DeviceInfo.ts";

type On = <T extends EventType>(type: T, handler: (message: EventMessage<T>) => void) => () => void;

/** Handlers for the device:* answers a native integration (Tauri, Samsung) sends back. `tag` prefixes the logs. */
export function registerDeviceEventHandlers(on: On, tag: string): void {
    on(EventType.ScreenshotResponse, async (message) => {
        const store = useStore();
        const payload = message.payload;
        console.info(`[${tag}] Received capture response with id ${payload.requestId}.`);
        await store.socketManager.sendScreenDump(new Uint8Array(payload.imageBase64), payload.deviceId);
    });

    on(EventType.DeviceDisconnected, (message) => {
        if (typeof window === "undefined") return;
        window.dispatchEvent(new CustomEvent("app:hard-failure", {
            detail: {
                source: "native",
                reason: `device_disconnected:${message.payload.deviceId}`,
                deviceId: message.payload.deviceId,
            },
        }));
    });

    on(EventType.MonitorStatus, (message) => {
        const {manufacturer, productCode, profile, power, backlight, source, error} = message.payload;
        if (error) {
            // A read error doesn't invalidate the rest of the payload, so
            // report what came through and carry the error in `additions`.
            console.warn(`[${tag}] Monitor status reported an error: ${error}`);
        }
        const store = useStore();
        store.socketManager.updateMonitorInfo({
            // The proto field is `manufacture`; the native payload spells it out.
            manufacture: manufacturer,
            product_code: productCode,
            power,
            backlight,
            source,
            // `profile` and `error` have no proto field of their own.
            additions: packMonitorAdditions({profile, error}),
        });
    });

    on(EventType.AuditResponse, (message) => {
        const {requestId, uploaded, success, error} = message.payload;
        if (!success) {
            console.warn(`[${tag}] Audit upload ${requestId} failed: ${error ?? "unknown error"}`);
            return;
        }
        console.info(`[${tag}] Audit upload ${requestId} uploaded ${uploaded} capture(s).`);
    });

    on(EventType.MonitorResponse, (message) => {
        const {requestId, data} = message.payload;
        console.info(`[${tag}] Monitor response ${requestId}${data ? `: ${data}` : ""}`);
    });

    on(EventType.Error, (message) => {
        const {requestId, code, message: text} = message.payload;
        console.warn(`[${tag}] Device error${requestId ? ` (${requestId})` : ""}: ${code}${text ? ` ${text}` : ""}`);
    });
}
